import { z } from 'zod';

/**
 * External test-suite ingest contract — the provider-agnostic report shape that
 * every producer (our hosted runner now; a customer's CI later) emits, and that
 * the ingest API validates. Kept deliberately small: raw per-test results +
 * a normalized step timeline + artifact references. The durable per-test
 * catalog, flaky-vs-fail classification, and read-model projection are computed
 * server-side from these (see the design doc). Storage lives in
 * `packages/db/src/schema/external-test.ts`.
 */

export const EXTERNAL_TEST_PROVIDERS = ['selenium', 'playwright', 'cypress', 'junit'] as const;
export type ExternalTestProvider = (typeof EXTERNAL_TEST_PROVIDERS)[number];

/**
 * Per-attempt wall-clock cap applied when a runner sets no `shardTimeoutMs`.
 *
 * Lives here rather than in the API because the config UI has to *name* the
 * resolved value — a bare `0` in a timeout field reads as "no limit", which is
 * the opposite of what it means. Two copies of this number would drift, and the
 * drift would surface only as a suite killed earlier than its own settings page
 * claimed. `run.job.ts` re-exports it, so API callers are unchanged.
 */
export const DEFAULT_SHARD_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Ceiling on a runner's configured `shardTimeoutMs`. Policy: a slice that cannot
 * finish in four hours wants more shards, not more patience. Raised from two
 * hours when three directory areas of a 53-area customer suite ran into the cap
 * once per-test retries started re-running failures; every other runtime bound
 * on the sandbox path (lease TTL, action and SSM timeouts, poll deadline) is
 * derived from the configured value, so this is the only number to move. The
 * pool maintenance reaper's 12 h is counted from the instance's original pool
 * claim, so a reused idle instance claimed more than ~8 h earlier could still be
 * reaped mid-area — rare (it needs chained leases on one instance), and
 * pre-existing at the old cap with ~10 h of margin.
 */
export const MAX_SHARD_TIMEOUT_MS = 4 * 60 * 60 * 1000;

/** Durable outbox job that moves a superseded uploaded suite bundle onto lifecycle retention. */
export const EXTERNAL_TEST_BUNDLE_RETIREMENT_JOB_TYPE = 'external-test.bundle-retirement';
export const EXTERNAL_AREA_VERIFICATION_JOB_TYPE = 'external-test.verify-areas';

/** Raw per-attempt result status as reported by the framework. */
export const EXTERNAL_TEST_RESULT_STATUSES = ['passed', 'failed', 'skipped'] as const;
export type ExternalTestResultStatus = (typeof EXTERNAL_TEST_RESULT_STATUSES)[number];

/** Coarse category for a normalized step (drives icons/grouping in the UI). */
export const EXTERNAL_TEST_STEP_CATEGORIES = [
  'navigate',
  'click',
  'type',
  'assert',
  'api',
  'other',
] as const;
export type ExternalTestStepCategory = (typeof EXTERNAL_TEST_STEP_CATEGORIES)[number];

export const EXTERNAL_TEST_ARTIFACT_KINDS = [
  'report',
  'video',
  'trace',
  'log',
  'screenshot',
] as const;
export type ExternalTestArtifactKind = (typeof EXTERNAL_TEST_ARTIFACT_KINDS)[number];

/** Source location in the authored feature file. */
export const ExternalTestLocationSchema = z.object({
  file: z.string().min(1).max(500),
  line: z.number().int().nonnegative(),
});
export type ExternalTestLocation = z.infer<typeof ExternalTestLocationSchema>;

/** One attempt's own wall-clock execution window. */
export const ExternalTestAttemptTimingSchema = z.object({
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
});
export type ExternalTestAttemptTiming = z.infer<typeof ExternalTestAttemptTimingSchema>;

/** Resolved authored input attached to a Gherkin step. */
export const ExternalTestBddArgumentSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('docString'),
    content: z.string().max(64 * 1024),
    mediaType: z.string().max(200).nullable(),
  }),
  z.object({
    kind: z.literal('dataTable'),
    rows: z.array(z.array(z.string().max(16 * 1024)).max(100)).max(100),
  }),
]);
export type ExternalTestBddArgument = z.infer<typeof ExternalTestBddArgumentSchema>;

/** BDD step details kept separate from provider execution identity. */
export const ExternalTestBddStepSchema = z.object({
  keyword: z.string().max(100),
  location: ExternalTestLocationSchema,
  background: z.boolean(),
  argument: ExternalTestBddArgumentSchema.nullable(),
});
export type ExternalTestBddStep = z.infer<typeof ExternalTestBddStepSchema>;

const ExternalTestBddNodeSchema = z.object({
  name: z.string().max(2_000),
  description: z.string().max(16 * 1024),
  location: ExternalTestLocationSchema,
});

const MAX_PROPERTIES = 30;
const MAX_PROPERTY_LABEL = 100;
const MAX_PROPERTY_VALUE = 500;

/**
 * One annotation rendered as a label/value pair (e.g. Tenant ID: tenant-1). The
 * shape only: `normalizeTestProperties` owns the limits and truncates rather than
 * rejects, so one long annotation cannot fail the whole result batch.
 */
const ExternalTestPropertySchema = z.object({
  label: z.string(),
  value: z.string(),
});
export type ExternalTestProperty = z.infer<typeof ExternalTestPropertySchema>;

/** Authored and resolved Gherkin context for one executed scenario/pickle. */
export const ExternalTestBddContextSchema = z.object({
  feature: ExternalTestBddNodeSchema,
  scenario: ExternalTestBddNodeSchema,
  tags: z.array(z.string().min(1).max(200)).max(50),
  rule: ExternalTestBddNodeSchema.nullable(),
  example: z
    .object({
      line: z.number().int().nonnegative(),
      column: z.number().int().nonnegative(),
      values: z
        .array(z.object({ column: z.string().max(2_000), value: z.string().max(16 * 1024) }))
        .max(100),
    })
    .nullable(),
});
export type ExternalTestBddContext = z.infer<typeof ExternalTestBddContextSchema>;

/** Return elapsed wall time only when both valid instants were recorded. */
export function elapsedTestAttemptMs(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): number | null {
  if (!startedAt || !finishedAt) return null;
  const start = Date.parse(startedAt);
  const finish = Date.parse(finishedAt);
  return Number.isFinite(start) && Number.isFinite(finish) && finish >= start
    ? finish - start
    : null;
}

/** One structured error from a failing test. */
export const ExternalTestErrorSchema = z.object({
  message: z.string().optional(),
  stack: z.string().optional(),
  type: z.string().optional(),
});
export type ExternalTestError = z.infer<typeof ExternalTestErrorSchema>;

/** A normalized step within a test — powers the left-nav timeline + video scrub. */
export const ExternalTestStepSchema = z.object({
  index: z.number().int().nonnegative(),
  title: z.string(),
  status: z.enum(EXTERNAL_TEST_RESULT_STATUSES),
  category: z.enum(EXTERNAL_TEST_STEP_CATEGORIES).default('other'),
  startMs: z.number().nonnegative().optional(),
  durationMs: z.number().nonnegative().optional(),
  /** Reference (storage key) to a screenshot captured at this step. */
  screenshotRef: z.string().optional(),
  /** Reference (storage key) to a DOM snapshot captured at this step. */
  domRef: z.string().optional(),
  /** BDD-authored step metadata; absent for ordinary provider steps. */
  bdd: ExternalTestBddStepSchema.optional(),
});
export type ExternalTestStep = z.infer<typeof ExternalTestStepSchema>;

/**
 * A text-only attachment captured by the framework (e.g. a Cucumber data
 * dump, a JSON payload). Binary attachments (screenshots/videos/traces) are
 * artifacts, handled by {@link ExternalTestArtifactRefSchema}, not this.
 */
export const ExternalTestOutputAttachmentSchema = z.object({
  name: z.string().min(1).max(200),
  contentType: z.string().min(1).max(100),
  /** Capped well below the batch body limit — this rides inline, not via S3. */
  body: z.string().max(16 * 1024),
});
export type ExternalTestOutputAttachment = z.infer<typeof ExternalTestOutputAttachmentSchema>;

/** A test's own stdout/stderr and text attachments (capped; see the boundary reporter). */
export const ExternalTestResultOutputSchema = z.object({
  stdout: z
    .string()
    .max(70 * 1024)
    .optional(),
  stderr: z
    .string()
    .max(70 * 1024)
    .optional(),
  attachments: z.array(ExternalTestOutputAttachmentSchema).max(20).optional(),
});
export type ExternalTestResultOutput = z.infer<typeof ExternalTestResultOutputSchema>;

const MAX_TAGS = 50;
// Playwright's built-in annotation types carry no label/value meaning.
const PLAYWRIGHT_INTERNAL_ANNOTATIONS = new Set(['skip', 'fixme', 'slow', 'fail']);
const MAX_TAG_LENGTH = 200;
const MAX_OUTPUT_CHARS = 70 * 1024;
const MAX_ATTACHMENTS = 20;
const MAX_ATTACHMENT_CHARS = 16 * 1024;

function clampText(text: string, max: number): string {
  if (text.length <= max) return text;
  // The marker takes room from the kept text, so the count covers it too.
  const markerFor = (cut: number) => `\n… [truncated ${cut} chars]`;
  const kept = max - markerFor(text.length).length;
  return text.slice(0, kept) + markerFor(text.length - kept);
}

/** Control characters (incl. newlines and ANSI escapes) become spaces: values are printed by the CLI. */
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]+/g;

/**
 * Annotations as stored: {label, value} pairs in report order, with Playwright's
 * internal annotation types and entries without a value dropped, control
 * characters flattened, text truncated (never rejected), and exact duplicates
 * removed.
 */
export function normalizeTestProperties(value: unknown): ExternalTestProperty[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  const properties: ExternalTestProperty[] = [];
  for (const entry of value) {
    if (properties.length >= MAX_PROPERTIES) break;
    if (!entry || typeof entry !== 'object') continue;
    const { label: rawLabel, value: rawText } = entry as Record<string, unknown>;
    if (typeof rawLabel !== 'string' || typeof rawText !== 'string') continue;
    const label = rawLabel.replace(CONTROL_CHARS, ' ').trim();
    const text = rawText.replace(CONTROL_CHARS, ' ').trim();
    if (label.length === 0 || text.length === 0) continue;
    if (PLAYWRIGHT_INTERNAL_ANNOTATIONS.has(label)) continue;
    const property = {
      label: label.slice(0, MAX_PROPERTY_LABEL),
      value: text.slice(0, MAX_PROPERTY_VALUE),
    };
    const key = JSON.stringify([property.label, property.value]);
    if (seen.has(key)) continue;
    seen.add(key);
    properties.push(property);
  }
  return properties.length > 0 ? properties : undefined;
}

/**
 * Tags as stored: strings only, deduplicated, clamped to the ingest schema's
 * limits. Container output is not schema-validated on the way in, so every
 * writer normalizes through this and the read side can trust the shape.
 */
export function normalizeTestTags(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const tags = [
    ...new Set(value.filter((tag): tag is string => typeof tag === 'string' && tag.length > 0)),
  ]
    .slice(0, MAX_TAGS)
    .map((tag) => tag.slice(0, MAX_TAG_LENGTH));
  return tags.length > 0 ? tags : undefined;
}

/**
 * Output as stored: the {@link ExternalTestResultOutputSchema} shape and
 * limits, whatever the container wrote. Runs after secret scrubbing too, which
 * can lengthen text (`[REDACTED]` outgrows a short secret).
 */
export function normalizeTestOutput(value: unknown): ExternalTestResultOutput | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const output = value as Record<string, unknown>;
  const out: ExternalTestResultOutput = {};
  if (typeof output.stdout === 'string' && output.stdout.length > 0) {
    out.stdout = clampText(output.stdout, MAX_OUTPUT_CHARS);
  }
  if (typeof output.stderr === 'string' && output.stderr.length > 0) {
    out.stderr = clampText(output.stderr, MAX_OUTPUT_CHARS);
  }
  if (Array.isArray(output.attachments)) {
    const attachments = output.attachments
      .flatMap((entry) => {
        if (!entry || typeof entry !== 'object') return [];
        const attachment = entry as Record<string, unknown>;
        if (
          typeof attachment.contentType !== 'string' ||
          attachment.contentType.length === 0 ||
          typeof attachment.body !== 'string'
        ) {
          return [];
        }
        const name =
          typeof attachment.name === 'string' && attachment.name.length > 0
            ? attachment.name
            : 'attachment';
        return [
          {
            name: name.slice(0, 200),
            contentType: attachment.contentType.slice(0, 100),
            body: clampText(attachment.body, MAX_ATTACHMENT_CHARS),
          },
        ];
      })
      .slice(0, MAX_ATTACHMENTS);
    if (attachments.length > 0) out.attachments = attachments;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** A location as stored, or undefined when the shape is not one. */
export function normalizeTestLocation(value: unknown): ExternalTestLocation | undefined {
  const parsed = ExternalTestLocationSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/** One test's result within a shard (one attempt). */
export const ExternalTestResultSchema = z.object({
  /**
   * Provider-immutable stable identity (repo + normalized spec path + describe/it
   * title path). NOT `file#title`. Used to attribute history to the durable test.
   */
  testKey: z.string().min(1),
  displayName: z.string().min(1),
  /** describe → it hierarchy (suite → section → scenario → action). */
  suitePath: z.array(z.string()).default([]),
  status: z.enum(EXTERNAL_TEST_RESULT_STATUSES),
  durationMs: z.number().nonnegative().optional(),
  /** 1-based attempt index (retries append). */
  attempt: z.number().int().positive().default(1),
  error: ExternalTestErrorSchema.optional(),
  steps: z.array(ExternalTestStepSchema).default([]),
  /**
   * Framework tags (e.g. playwright-bdd's mapped Gherkin `@tags`). Optional:
   * only providers that expose tags on the test send this.
   */
  tags: z.array(z.string().min(1).max(200)).max(50).optional(),
  /**
   * Ordered label/value pairs from Playwright `testInfo.annotations`
   * (`type` -> label, `description` -> value). Optional: absent when empty.
   */
  properties: z.array(ExternalTestPropertySchema).optional(),
  /** Where the test is defined. Optional: only Playwright reports this today. */
  location: ExternalTestLocationSchema.optional(),
  /** The test's own stdout/stderr/attachments. Optional: absent when empty. */
  output: ExternalTestResultOutputSchema.optional(),
  /** Exact execution window; absent when a provider did not emit boundaries. */
  timing: ExternalTestAttemptTimingSchema.optional(),
  /** Resolved Gherkin context; absent for non-BDD providers. */
  bdd: ExternalTestBddContextSchema.optional(),
});
export type ExternalTestResult = z.infer<typeof ExternalTestResultSchema>;

/** Reference to an uploaded artifact (bytes are PUT to a presigned URL first). */
export const ExternalTestArtifactRefSchema = z.object({
  kind: z.enum(EXTERNAL_TEST_ARTIFACT_KINDS),
  storageKey: z.string().min(1),
  /** Optional link to the specific test this artifact belongs to. */
  testKey: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
});
export type ExternalTestArtifactRef = z.infer<typeof ExternalTestArtifactRefSchema>;

/** Manifest posted when a run (or a shard within it) opens. */
export const ExternalTestRunManifestSchema = z.object({
  runnerId: z.string().uuid(),
  provider: z.enum(EXTERNAL_TEST_PROVIDERS),
  /** The framework/CI-side run id, if any (used for idempotent grouping). */
  providerRunId: z.string().optional(),
  commit: z.string().optional(),
  branch: z.string().optional(),
  /** Pinned commit SHA the shards ran against. */
  repoRevision: z.string().optional(),
  targetUrl: z.string().url().optional(),
  shardIndex: z.number().int().nonnegative().optional(),
  shardTotal: z.number().int().positive().optional(),
  startedAt: z.string().datetime().optional(),
});
export type ExternalTestRunManifest = z.infer<typeof ExternalTestRunManifestSchema>;

/** Body for `POST /api/v1/external-test-runs/:id/results` (idempotent append). */
export const ExternalTestResultsBatchSchema = z.object({
  shardIndex: z.number().int().nonnegative().optional(),
  results: z.array(ExternalTestResultSchema).min(1),
  artifacts: z.array(ExternalTestArtifactRefSchema).default([]),
});
export type ExternalTestResultsBatch = z.infer<typeof ExternalTestResultsBatchSchema>;

/**
 * Cross-attempt verdict computed server-side at seal. `flaky` = failed at least
 * one attempt but passed a later one; `failed` = failed every attempt
 * (definitive — the only verdict that triggers triage).
 */
export const EXTERNAL_TEST_VERDICTS = ['passed', 'flaky', 'failed', 'skipped'] as const;
export type ExternalTestVerdict = (typeof EXTERNAL_TEST_VERDICTS)[number];

/** Rolled-up reliability of a durable test over its history. */
export const EXTERNAL_TEST_HEALTHS = ['unverified', 'passing', 'flaky', 'failing'] as const;
export type ExternalTestHealth = (typeof EXTERNAL_TEST_HEALTHS)[number];
